"use client";
import { ProductImage, SingleProduct } from "@/__generated__/sdk";
import ShareProduct from "./ShareProduct";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, Fade, Icon, IconButton, Stack, SxProps, Theme, Typography } from "@mui/material";
import Image from "next/image";
import ProductGallery, { ProductGallerySlide } from "./ProductGallery";
import useEmblaCarousel from "embla-carousel-react";
import { usePrevNextButtons } from "@/lib/usePrevNextButtons";
import { EmblaCarouselType } from "embla-carousel";
import Arrow2LeftIcon from "@/app/_components/Icons/Arrow2LeftIcon";
import Arrow2RightIcon from "@/app/_components/Icons/Arrow2RightIcon";
import { SVideoDto } from "@/__generated__/graphql";
import { PlayCircle } from "@mui/icons-material";
import { imagePlaceholder } from "@/lib/imagePlaceholder";

type Props = {
  product: SingleProduct;
  productTitle: string;
  images: ProductImage[];
  videos: SVideoDto[];
};

type GalleryItem =
  | { image: ProductImage; type: "image" }
  | { video: SVideoDto; type: "video" };

type GalleryEntry = { item: GalleryItem; index: number };

const mosaicGap = "6px";
const mosaicSmallWidth = "calc((100cqi - 12px) / 2.5)";
const mosaicLargeWidth = "calc(2 * (100cqi - 12px) / 2.5 + 6px)";

function mediaAlt(item: GalleryItem, productTitle: string, index: number) {
  if (item.type === "video") return `ویدئو ${productTitle}`;
  return item.image.alt?.trim() || (index === 0 ? productTitle : `${productTitle} - تصویر ${index + 1}`);
}

function MosaicGroup({
  group,
  productTitle,
  onOpen,
}: {
  group: GalleryEntry[];
  productTitle: string;
  onOpen: (index: number) => void;
}) {
  const [primary, secondary, tertiary] = group;
  const tileSx = { aspectRatio: "1 / 1", height: "auto" } as const;

  return (
    <Box sx={{ display: "flex", gap: mosaicGap, alignItems: "flex-start", flexShrink: 0 }}>
      <Box sx={{ width: mosaicLargeWidth, flexShrink: 0 }}>
        <GalleryMedia
          entry={primary}
          productTitle={productTitle}
          // first mosaic group is above the fold on mobile — don't lazy it
          priority={primary.index < 3}
          sizes="(max-width: 899px) 72vw, 40vw"
          onOpen={onOpen}
          sx={tileSx}
        />
      </Box>
      {secondary && tertiary ? (
        <Box
          sx={{
            width: mosaicSmallWidth,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: mosaicGap,
          }}
        >
          <GalleryMedia
            entry={secondary}
            productTitle={productTitle}
            priority={secondary.index < 3}
            dense
            sizes="(max-width: 899px) 34vw, 20vw"
            onOpen={onOpen}
            sx={tileSx}
          />
          <GalleryMedia
            entry={tertiary}
            productTitle={productTitle}
            priority={tertiary.index < 3}
            dense
            sizes="(max-width: 899px) 34vw, 20vw"
            onOpen={onOpen}
            sx={tileSx}
          />
        </Box>
      ) : secondary ? (
        // leftover without stack pair → same large tile, not a lonely small one
        <Box sx={{ width: mosaicLargeWidth, flexShrink: 0 }}>
          <GalleryMedia
            entry={secondary}
            productTitle={productTitle}
            priority={secondary.index < 3}
            sizes="(max-width: 899px) 72vw, 40vw"
            onOpen={onOpen}
            sx={tileSx}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function GalleryMedia({
  entry,
  productTitle,
  priority = false,
  sizes,
  dense = false,
  onOpen,
  sx,
}: {
  entry: GalleryEntry;
  productTitle: string;
  priority?: boolean;
  sizes: string;
  dense?: boolean;
  onOpen: (index: number) => void;
  sx?: SxProps<Theme>;
}) {
  const { item, index } = entry;
  const isVideo = item.type === "video";
  const press = useRef({ x: 0, y: 0, moved: false });

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={isVideo ? "پخش ویدئو" : `تصویر ${index + 1}`}
      onPointerDown={(event) => {
        press.current = { x: event.clientX, y: event.clientY, moved: false };
      }}
      onPointerMove={(event) => {
        if (Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y) > 10) {
          press.current.moved = true;
        }
      }}
      onClick={() => {
        if (!press.current.moved) onOpen(index);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(index);
        }
      }}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 2,
        border: 0,
        p: 0,
        m: 0,
        width: "100%",
        height: "auto",
        aspectRatio: "1 / 1",
        display: "block",
        cursor: "grab",
        touchAction: "pan-x pan-y",
        bgcolor: "grey.A100",
        ...sx,
      }}
    >
      <Image
        alt={mediaAlt(item, productTitle, index)}
        src={item.type === "image" ? item.image.address : item.video.thumbnail}
        priority={priority}
        quality={80}
        loading={priority ? "eager" : "lazy"}
        sizes={sizes}
        placeholder={priority ? "empty" : imagePlaceholder}
        draggable={false}
        fill
        style={{ objectFit: "cover" }}
      />
      {isVideo && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.18)",
            color: "#fff",
          }}
        >
          <PlayCircle sx={{ fontSize: dense ? 28 : 40 }} />
        </Box>
      )}
    </Box>
  );
}

export default function ProductImagesView({ images, videos, productTitle, product }: Props) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
    loop: false,
    direction: "rtl",
    dragFree: false,
    align: "start",
  });
  // const emblaMainConRef = useRef<Element | null>(null);
  // const isHoveringEmblaMainRef = useHoverDirty(emblaMainConRef);
  const {
    nextBtnDisabled: emblaMainNextBtnDisabled,
    onNextButtonClick: emblaMainOnNextButtonClick,
    onPrevButtonClick: emblaMainOnPrevButtonClick,
    prevBtnDisabled: emblaMainPrevBtnDisabled,
  } = usePrevNextButtons(emblaMainApi as EmblaCarouselType | undefined);

  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    direction: "rtl",
  });
  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi],
  );
  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();

    emblaMainApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);

  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  const slideSpacing = "0px";
  const slideSize = "100%";
  const thumbsSlideSpacing = "8px";

  const items = useMemo(() => {
    const imageItems = images.map((image) => ({ image, type: "image" as const }));
    const videoItems = videos.map((video) => ({ video, type: "video" as const }));
    return [...imageItems, ...videoItems];
  }, [images, videos]);

  const mosaicGroups = useMemo(() => {
    const entries: GalleryEntry[] = items.map((item, index) => ({ item, index }));
    const groups: GalleryEntry[][] = [];
    for (let index = 0; index < entries.length; index += 3) {
      groups.push(entries.slice(index, index + 3));
    }
    return groups;
  }, [items]);

  const slides = useMemo<ProductGallerySlide[]>(
    () =>
      items.map((item) =>
        item.type === "image"
          ? { type: "image", src: item.image.address, thumb: item.image.address, alt: item.image.alt?.trim() ?? "" }
          : { type: "video", src: item.video.address, thumb: item.video.thumbnail, alt: "" },
      ),
    [items],
  );

  const openAt = useCallback((index: number) => {
    setGalleryIndex(index);
  }, []);

  const changeGalleryIndex = useCallback(
    (index: number) => {
      setGalleryIndex(index);
      emblaMainApi?.scrollTo(index);
    },
    [emblaMainApi],
  );

  return (
    <Stack>
      <Card elevation={0} sx={{ position: "relative", width: "100%" }}>
        <Box sx={{ position: "absolute", top: 8, insetInlineEnd: 8, zIndex: 2 }}>
          <ShareProduct product={product} />
        </Box>
        <Box data-mobile-gallery sx={{ display: { xs: "block", md: "none" }, containerType: "inline-size" }}>
          <Box
            data-mobile-gallery-scroll
            sx={{
              overflowX: "auto",
              overflowY: "hidden",
              overscrollBehaviorX: "contain",
              WebkitOverflowScrolling: "touch",
              // Allow vertical page scroll while still enabling horizontal mosaic swipe
              touchAction: "pan-x pan-y",
              cursor: "grab",
              scrollbarWidth: "none",
              borderRadius: 3,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box sx={{ display: "flex", gap: mosaicGap, width: "max-content", alignItems: "flex-start" }}>
              {mosaicGroups.map((group) => (
                <MosaicGroup key={group[0].index} group={group} productTitle={productTitle} onOpen={openAt} />
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "block" },
            "& .emblaMainNextBtn, & .emblaMainPrevBtn": {
              opacity: 1,
              display: "flex",
              transition: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)",
              "@media (hover: hover) and (pointer: fine)": {
                opacity: "0 !important",
              },
            },
            "&:hover .emblaMainNextBtn, &:hover .emblaMainPrevBtn": {
              opacity: "1 !important",
            },
          }}
        >
          <Box sx={{ overflow: "hidden", borderRadius: 3, position: "relative" }} ref={emblaMainRef}>
            <Box
              data-media
              sx={{
                display: "flex",
                touchAction: "pan-y pinch-zoom",
                marginLeft: `calc(${slideSpacing} * -1)`,
              }}
            >
              {items.map((item, index) => {
                return (
                  <Box
                    key={index}
                    sx={{
                      transform: "translate3d(0, 0, 0)",
                      flex: `0 0 ${slideSize}`,
                      minWidth: 0,
                      paddingLeft: slideSpacing,
                    }}
                  >
                    <Box
                      component={"figure"}
                      onPointerDown={(event) => {
                        event.currentTarget.dataset.x = String(event.clientX);
                        event.currentTarget.dataset.y = String(event.clientY);
                        event.currentTarget.dataset.moved = "0";
                      }}
                      onPointerMove={(event) => {
                        const startX = Number(event.currentTarget.dataset.x);
                        const startY = Number(event.currentTarget.dataset.y);
                        if (Math.hypot(event.clientX - startX, event.clientY - startY) > 10) {
                          event.currentTarget.dataset.moved = "1";
                        }
                      }}
                      onClick={(event) => {
                        if (event.currentTarget.dataset.moved === "1") return;
                        openAt(index);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openAt(index);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={item.type === "video" ? "پخش ویدئو" : `بزرگ‌نمایی تصویر ${index + 1}`}
                      sx={{
                        aspectRatio: 1,
                        overflow: "hidden",
                        borderRadius: 3,
                        userSelect: "none",
                        position: "relative",
                        width: "100%",
                        m: 0,
                        cursor: "zoom-in",
                        bgcolor: "grey.A100",
                      }}
                    >
                      <Image
                        alt={mediaAlt(item, productTitle, index)}
                        src={item.type === "image" ? item.image.address : item.video.thumbnail}
                        {...(item.type === "image"
                          ? {
                              "data-image_url": item.image.address,
                            }
                          : {
                              "data-video_url": item.video.address,
                            })}
                        priority={index === 0}
                        quality={80}
                        loading={index === 0 ? "eager" : "lazy"}
                        sizes="(max-width: 899px) 72vw, (max-width: 1280px) 42vw, 36vw"
                        placeholder={index === 0 ? "empty" : imagePlaceholder}
                        draggable={false}
                        fill
                        style={{ objectFit: "cover" }}
                      />
                      {item.type === "video" && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255,255,255,0.2)",
                            backdropFilter: "blur(4px)",
                            overflow: "hidden",
                            borderRadius: 3,
                            color: (t) => t.palette.grey["800"],
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                              backdropFilter: "blur(5px)",
                              backgroundColor: "rgba(255,255,255,0.7)",
                              borderRadius: 50,
                              p: 0.2,
                              pr: 1,
                            }}
                          >
                            <PlayCircle sx={{ fontSize: 42 }} />
                            <Typography>مشاهده ویدئو</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Fade in={!emblaMainNextBtnDisabled}>
              <Box
                className="emblaMainNextBtn"
                sx={{
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  right: 8,
                  top: 0,
                  height: "100%",
                  zIndex: 1,
                }}
              >
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    emblaMainOnNextButtonClick();
                  }}
                  aria-label="تصویر بعدی"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.9)",
                    width: 36,
                    height: 36,
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <Icon
                    sx={{
                      "& svg": {
                        filter: "drop-shadow(rgba(255, 255, 255, 1) 1px 0px 0.5px)",
                      },
                    }}
                  >
                    <Arrow2LeftIcon />
                  </Icon>
                </IconButton>
              </Box>
            </Fade>
            <Fade in={!emblaMainPrevBtnDisabled}>
              <Box
                className="emblaMainPrevBtn"
                sx={{
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  left: 8,
                  top: 0,
                  height: "100%",
                  zIndex: 1,
                }}
              >
                <IconButton
                  aria-label="تصویر قبلی"
                  onClick={(event) => {
                    event.stopPropagation();
                    emblaMainOnPrevButtonClick();
                  }}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.9)",
                    width: 36,
                    height: 36,
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <Icon
                    sx={{
                      "& svg": {
                        filter: "drop-shadow(rgba(255, 255, 255, 1) -1px 0px 0.5px)",
                      },
                    }}
                  >
                    <Arrow2RightIcon />
                  </Icon>
                </IconButton>
              </Box>
            </Fade>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "block" },
            marginTop: thumbsSlideSpacing,
          }}
        >
          <Box sx={{ overflow: "hidden", borderRadius: 3, position: "relative" }} ref={emblaThumbsRef}>
            <Box sx={{ display: "flex", flexDirection: "row", marginLeft: `calc(${thumbsSlideSpacing} * -1)` }}>
              {items.map((item, index) => {
                const selected = index === selectedIndex;
                return (
                  <Box
                    key={index}
                    component="button"
                    type="button"
                    aria-label={item.type === "video" ? "پخش ویدئو" : `تصویر ${index + 1}`}
                    aria-current={selected ? "true" : undefined}
                    onClick={() => {
                      onThumbClick(index);
                      if (item.type === "video") openAt(index);
                    }}
                    className="embla-thumbs__slide"
                    sx={{
                      flex: { xs: "0 0 64px", sm: "0 0 72px" },
                      minWidth: 0,
                      p: 0,
                      paddingLeft: thumbsSlideSpacing,
                      border: 0,
                      bgcolor: "transparent",
                      lineHeight: 0,
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      sx={(theme) => ({
                        m: 0,
                        overflow: "hidden",
                        borderRadius: 2,
                        position: "relative",
                        userSelect: "none",
                        aspectRatio: 1,
                        width: "100%",
                        transition: "box-shadow 0.2s ease, opacity 0.2s ease",
                        opacity: selected ? 1 : 0.72,
                        boxShadow: selected ? `inset 0 0 0 2px ${theme.palette.secondary.main}` : "inset 0 0 0 1px rgba(0,0,0,0.08)",
                      })}
                    >
                      <Image
                        alt=""
                        src={item.type === "image" ? item.image.address : item.video.thumbnail}
                        placeholder={imagePlaceholder}
                        draggable={false}
                        priority={false}
                        sizes="(max-width: 600px) 20vw, (max-width: 900px) 14vw, 10vw"
                        quality={65}
                        loading="lazy"
                        fill
                        style={{ objectFit: "cover" }}
                      />
                      {item.type === "video" && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0,0,0,0.18)",
                            color: "#fff",
                          }}
                        >
                          <PlayCircle sx={{ fontSize: 28 }} />
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Card>
      {galleryIndex != null && (
        <ProductGallery slides={slides} index={galleryIndex} onIndexChange={changeGalleryIndex} onClose={() => setGalleryIndex(null)} />
      )}
    </Stack>
  );
}
